import { useState } from "react";

/**
 * Numeric entry with no keyboard. On a tablet the software keyboard
 * covers half the screen and takes a moment to appear; on a gloved hand
 * its keys are too small. Four fixed targets are faster and more
 * reliable than any text field.
 */
export function PinPad({
  length = 4,
  onSubmit,
  disabled = false,
  error,
}: {
  length?: number;
  onSubmit: (pin: string) => void;
  disabled?: boolean;
  error?: string | undefined;
}) {
  const [pin, setPin] = useState("");

  const press = (digit: string) => {
    if (disabled) return;
    const next = pin + digit;
    setPin(next);
    if (next.length >= length) {
      onSubmit(next);
      setPin("");
    }
  };

  return (
    <div className="pinpad">
      <div className="pinpad-dots" aria-label={`${pin.length} of ${length} digits`}>
        {Array.from({ length }, (_, i) => (
          <span key={i} className={i < pin.length ? "dot filled" : "dot"} />
        ))}
      </div>

      {/* The line is always there, empty or not, so the keys below do not
          move when "Wrong PIN." appears under a thumb already on its way
          to the next digit. */}
      <div className="pinpad-message">
        {error ? <p className="error" role="alert">{error}</p> : null}
      </div>

      <div className="pinpad-keys">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} type="button" onClick={() => press(d)} disabled={disabled}>
            {d}
          </button>
        ))}
        <button
          type="button"
          className="ghost"
          onClick={() => setPin("")}
          disabled={disabled || pin.length === 0}
        >
          Clear
        </button>
        <button type="button" onClick={() => press("0")} disabled={disabled}>
          0
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => setPin(pin.slice(0, -1))}
          disabled={disabled || pin.length === 0}
          aria-label="Delete last digit"
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 5H9l-6 7 6 7h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" />
            <path d="M17 9l-6 6" />
            <path d="M11 9l6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
